/**
 * Tiny line-level diff (T7 — plan §7.4 ``rewriter.proposed`` viewer).
 *
 * The audit viewer's ``rewriter.proposed`` detail panel renders an
 * inline before/after diff for each rewrite the architect picked at
 * G4. Requirements text is short (usually 1–5 lines), so a full
 * ``diff`` npm dep is overkill — this LCS-based walker produces the
 * same per-line ``+`` / ``-`` / context markers in ~40 lines and
 * keeps the SPA's dependency footprint flat.
 *
 * The output is intentionally line-level, not character-level: a
 * requirement rewrite typically replaces an entire clause, and a
 * char-level diff on natural-language sentences is noisy without a
 * tokenizer that respects word boundaries. ``DiffOp[]`` is dense
 * enough to drive a stacked red/green block view in the audit viewer
 * and small enough to ship without a third-party render lib.
 */
export type DiffOp = {
  kind: 'add' | 'remove' | 'context'
  text: string
}

/** Compute a line-level diff between ``before`` and ``after``.
 *
 *  Returns an arrival-ordered sequence of operations:
 *    * ``context`` — line is unchanged (present in both)
 *    * ``remove``  — line was in ``before`` but not in ``after``
 *    * ``add``     — line is in ``after`` but not in ``before``
 *
 *  Empty inputs are tolerated. Identical inputs return an array of
 *  ``context`` ops (one per line). The walker is O(m·n) in line
 *  count which is fine for the requirement-text payloads this is
 *  used for; a large-payload caller should pre-trim or chunk.
 */
export function lineDiff(before: string, after: string): DiffOp[] {
  const a = before === '' ? [] : before.split('\n')
  const b = after === '' ? [] : after.split('\n')
  const m = a.length
  const n = b.length

  // LCS table — ``lcs[i][j]`` is the length of the longest common
  // subsequence of ``a[0..i)`` and ``b[0..j)``.
  const lcs: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  )
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      lcs[i][j] =
        a[i - 1] === b[j - 1]
          ? lcs[i - 1][j - 1] + 1
          : Math.max(lcs[i - 1][j], lcs[i][j - 1])
    }
  }

  // Walk back from (m, n) to (0, 0) emitting ops in reverse.
  const ops: DiffOp[] = []
  let i = m
  let j = n
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      ops.push({ kind: 'context', text: a[i - 1] })
      i--
      j--
    } else if (lcs[i][j - 1] >= lcs[i - 1][j]) {
      // Tie-broken toward ``add`` in walk-back so the reversed
      // output emits ``remove`` before ``add`` at the same position
      // — matches the conventional diff display ("-" above "+").
      ops.push({ kind: 'add', text: b[j - 1] })
      j--
    } else {
      ops.push({ kind: 'remove', text: a[i - 1] })
      i--
    }
  }
  while (i > 0) {
    ops.push({ kind: 'remove', text: a[i - 1] })
    i--
  }
  while (j > 0) {
    ops.push({ kind: 'add', text: b[j - 1] })
    j--
  }
  return ops.reverse()
}

/** Coarse summary counts for a diff. Used by the audit viewer's
 *  detail header (``+3 / -1``) without making consumers walk the
 *  ops twice. */
export interface DiffSummary {
  added: number
  removed: number
  context: number
}

export function summariseDiff(ops: DiffOp[]): DiffSummary {
  let added = 0
  let removed = 0
  let context = 0
  for (const op of ops) {
    if (op.kind === 'add') added++
    else if (op.kind === 'remove') removed++
    else context++
  }
  return { added, removed, context }
}
